import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Calendar, Users, Globe, Trash2, Edit, LogOut, Eye, EyeOff, UserPlus, X, ChevronRight, Copy, ExternalLink, Shield, Mail, Lock, User, Check, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ThemeToggle } from "@/components/ThemeToggle";

import { BACKEND_URL as API } from '../utils/apiBase';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("events");
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [duplicatingEvent, setDuplicatingEvent] = useState(null);
  const [dupData, setDupData] = useState({ name: "", slug: "", description: "" });
  const [newEvent, setNewEvent] = useState({ name: "", slug: "", description: "" });
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", auth_provider: "local", role: "event_user" });
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [evRes, usRes] = await Promise.all([
        axios.get(`${API}/api/admin/events`),
        axios.get(`${API}/api/admin/users`)
      ]);
      setEvents(evRes.data);
      setUsers(usRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
    }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || "");
      setProfileEmail(user.email || "");
    }
  }, [user]);

  // === EVENT ACTIONS ===
  const createEvent = async () => {
    try {
      await axios.post(`${API}/api/admin/events`, newEvent);
      toast.success("Event created");
      setNewEvent({ name: "", slug: "", description: "" });
      setShowCreateEvent(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error creating event"); }
  };

  const updateEvent = async () => {
    try {
      await axios.put(`${API}/api/admin/events/${editingEvent.event_id}`, editingEvent);
      toast.success("Event updated");
      setEditingEvent(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error updating event"); }
  };

  const toggleEventStatus = async (event) => {
    const newStatus = event.status === "active" ? "inactive" : "active";
    try {
      await axios.put(`${API}/api/admin/events/${event.event_id}`, { status: newStatus });
      toast.success(`Event ${newStatus}`);
      loadData();
    } catch (err) { toast.error("Error updating event"); }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/admin/events/${eventId}`);
      toast.success("Event deleted");
      loadData();
    } catch (err) { toast.error("Error deleting event"); }
  };

  const duplicateEvent = async () => {
    if (!dupData.name.trim()) { toast.error("Event name is required"); return; }
    try {
      const res = await axios.post(`${API}/api/admin/events/${duplicatingEvent.event_id}/duplicate`, dupData);
      const cloned = res.data.cloned;
      toast.success(`Event duplicated (${cloned.judges} judges, theme: ${cloned.theme ? 'yes' : 'no'})`);
      setDuplicatingEvent(null);
      setDupData({ name: "", slug: "", description: "" });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error duplicating event"); }
  };

  // === USER ACTIONS ===
  const createUser = async () => {
    try {
      await axios.post(`${API}/api/admin/users`, newUser);
      toast.success("User created");
      setNewUser({ email: "", name: "", password: "", auth_provider: "local", role: "event_user" });
      setShowCreateUser(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error creating user"); }
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    try {
      const payload = {
        email: editingUser.email,
        name: editingUser.name,
        role: editingUser.role,
        auth_provider: editingUser.auth_provider,
        is_active: editingUser.is_active
      };
      if (editingUser.newPassword) payload.password = editingUser.newPassword;
      await axios.put(`${API}/api/admin/users/${editingUser.user_id}`, payload);

      // Handle event assignments
      const currentEvents = editingUser.event_ids || [];
      const targetEvents = editingUser.targetEventIds || [];
      const toAdd = targetEvents.filter(id => !currentEvents.includes(id));
      const toRemove = currentEvents.filter(id => !targetEvents.includes(id));
      for (const eid of toAdd) {
        await axios.post(`${API}/api/admin/events/${eid}/users`, { user_id: editingUser.user_id }).catch(() => {});
      }
      for (const eid of toRemove) {
        await axios.delete(`${API}/api/admin/events/${eid}/users/${editingUser.user_id}`).catch(() => {});
      }

      toast.success("User updated");
      setEditingUser(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error updating user"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      toast.success("User deleted");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error deleting user"); }
  };

  const assignUser = async (eventId, userId) => {
    try {
      await axios.post(`${API}/api/admin/events/${eventId}/users`, { user_id: userId });
      toast.success("User assigned");
      setShowAssignUser(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error assigning user"); }
  };

  const removeUserFromEvent = async (eventId, userId) => {
    try {
      await axios.delete(`${API}/api/admin/events/${eventId}/users/${userId}`);
      toast.success("User removed from event");
      loadData();
    } catch (err) { toast.error("Error removing user"); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const updateProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await axios.put(`${API}/api/auth/profile`, { name: profileName, email: profileEmail });
      toast.success("Profile updated");
      // Refresh auth state
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error updating profile"); }
    finally { setProfileLoading(false); }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setProfileLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Error changing password"); }
    finally { setProfileLoading(false); }
  };
  const slugify = (text) => text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim();
  const baseUrl = window.location.origin;
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  // Style constants
  const cardBg = "bg-card border border-border";
  const inputCls = "bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20";
  const selectCls = "w-full px-3 py-2 rounded-md bg-background border border-border text-foreground text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none";
  const labelCls = "text-foreground text-sm font-medium mb-1.5 block";
  const badgeCls = (color) => `px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-500/15 text-${color}-400 border border-${color}-500/20`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card px-6 py-4 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/wffa-logo-white.png" alt="WFFA" className="h-8 wffa-logo-adaptive" />
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">
                WFFA Admin
              </h1>
              <p className="text-muted-foreground text-xs">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground hover:text-foreground" data-testid="logout-btn">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Events", value: events.length, color: "purple" },
            { label: "Active", value: events.filter(e => e.status === 'active').length, color: "green" },
            { label: "Users", value: users.length, color: "blue" },
            { label: "Admins", value: users.filter(u => u.role === 'super_admin').length, color: "amber" }
          ].map(s => (
            <Card key={s.label} className={`p-4 text-center ${cardBg}`} data-testid={`stat-${s.label.toLowerCase()}`}>
              <p className={`text-3xl font-bold text-${s.color}-400`}>{s.value}</p>
              <p className="text-muted-foreground text-sm mt-1">{s.label}</p>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-3">
          {[
            { id: "events", icon: Calendar, label: "Events" },
            { id: "users", icon: Users, label: "Users" },
            { id: "profile", icon: Settings, label: "Profile" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-primary text-foreground shadow-lg shadow-purple-600/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* ============ EVENTS TAB ============ */}
        {activeTab === "events" && (
          <div>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-foreground">Events</h2>
              <Button onClick={() => setShowCreateEvent(true)} className="bg-primary hover:bg-primary/90 text-foreground font-medium" data-testid="create-event-btn">
                <Plus size={16} className="mr-2" /> New Event
              </Button>
            </div>

            {/* Create Event Form */}
            {showCreateEvent && (
              <Card className={`p-6 mb-6 ${cardBg}`} data-testid="create-event-form">
                <h3 className="text-lg font-semibold text-foreground mb-4">Create Event</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className={labelCls}>Name *</Label>
                    <Input value={newEvent.name} onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value, slug: slugify(e.target.value) })} className={inputCls} placeholder="World Finals 2026" data-testid="event-name-input" />
                  </div>
                  <div>
                    <Label className={labelCls}>Slug (URL)</Label>
                    <Input value={newEvent.slug} onChange={(e) => setNewEvent({ ...newEvent, slug: e.target.value })} className={inputCls} placeholder="world-finals-2026" data-testid="event-slug-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={labelCls}>Description</Label>
                    <Input value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} className={inputCls} placeholder="Optional description" />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <Button onClick={createEvent} className="bg-primary hover:bg-primary/90 text-foreground" data-testid="submit-event-btn">Create</Button>
                  <Button variant="outline" onClick={() => setShowCreateEvent(false)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground">Cancel</Button>
                </div>
              </Card>
            )}

            {/* Event List */}
            <div className="space-y-3">
              {events.map(ev => (
                <Card key={ev.event_id} className={`p-5 ${cardBg} ${ev.status !== 'active' ? 'opacity-50' : ''} transition-all hover:border-[#3a3a4a]`} data-testid={`event-card-${ev.event_id}`}>
                  {editingEvent?.event_id === ev.event_id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className={labelCls}>Name</Label><Input value={editingEvent.name} onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })} className={inputCls} /></div>
                        <div><Label className={labelCls}>Slug</Label><Input value={editingEvent.slug} onChange={(e) => setEditingEvent({ ...editingEvent, slug: e.target.value })} className={inputCls} /></div>
                        <div className="md:col-span-2"><Label className={labelCls}>Description</Label><Input value={editingEvent.description || ""} onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })} className={inputCls} /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={updateEvent} className="bg-green-600 hover:bg-green-700 text-foreground">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingEvent(null)} className="border-border text-foreground hover:bg-white/5">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-foreground">{ev.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ev.status === 'active' ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-gray-500/15 text-muted-foreground border border-gray-500/20'}`}>
                            {ev.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Globe size={13} /> /event/{ev.slug}</span>
                          <span className="flex items-center gap-1"><Users size={13} /> {ev.user_count || 0} users</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button size="sm" onClick={() => navigate(`/event/${ev.slug}/director`)} className="bg-primary hover:bg-primary/90 text-foreground h-8 px-3" data-testid={`open-event-${ev.event_id}`}>
                          <ChevronRight size={15} className="mr-1" /> Open
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setDuplicatingEvent(ev); setDupData({ name: `${ev.name} (copy)`, slug: `${ev.slug}-copy`, description: ev.description || "" }); }} className="border-border text-foreground hover:bg-white/5 hover:text-foreground h-8" data-testid={`dup-event-${ev.event_id}`} title="Duplicate event">
                          <Copy size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAssignUser(ev)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground h-8" data-testid={`assign-user-${ev.event_id}`} title="Assign users">
                          <UserPlus size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingEvent({ ...ev })} className="border-border text-foreground hover:bg-white/5 hover:text-foreground h-8" title="Edit">
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleEventStatus(ev)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground h-8" title={ev.status === 'active' ? 'Deactivate' : 'Activate'}>
                          {ev.status === 'active' ? <EyeOff size={14} /> : <Eye size={14} />}
                        </Button>
                        <Button size="sm" onClick={() => deleteEvent(ev.event_id)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/20 h-8" title="Delete">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Assign User Panel */}
                  {showAssignUser?.event_id === ev.event_id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-200">Manage User Access</h4>
                        <button onClick={() => setShowAssignUser(null)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
                      </div>
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-2">Available users:</p>
                        <div className="flex flex-wrap gap-2">
                          {users.filter(u => u.role !== 'super_admin' && !u.event_ids?.includes(ev.event_id)).map(u => (
                            <Button key={u.user_id} size="sm" variant="outline" onClick={() => assignUser(ev.event_id, u.user_id)} className="text-xs border-border text-foreground hover:bg-green-600/20 hover:text-green-400 hover:border-green-500/30">
                              <Plus size={12} className="mr-1" /> {u.name}
                            </Button>
                          ))}
                          {users.filter(u => u.role !== 'super_admin' && !u.event_ids?.includes(ev.event_id)).length === 0 && (
                            <span className="text-gray-600 text-xs italic">No users available to assign</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">Assigned users:</p>
                      <div className="flex flex-wrap gap-2">
                        {users.filter(u => u.event_ids?.includes(ev.event_id)).map(u => (
                          <span key={u.user_id} className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/15 border border-primary/20 rounded-lg text-xs text-primary">
                            {u.name}
                            <button onClick={() => removeUserFromEvent(ev.event_id, u.user_id)} className="hover:text-red-400 transition-colors"><X size={12} /></button>
                          </span>
                        ))}
                        {users.filter(u => u.event_ids?.includes(ev.event_id)).length === 0 && (
                          <span className="text-gray-600 text-xs italic">No users assigned</span>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
              {events.length === 0 && (
                <Card className={`p-10 text-center ${cardBg}`}>
                  <Calendar size={40} className="mx-auto mb-3 text-gray-600" />
                  <p className="text-muted-foreground">No events yet. Create your first event to get started.</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-foreground">Users</h2>
              <Button onClick={() => setShowCreateUser(true)} className="bg-primary hover:bg-primary/90 text-foreground font-medium" data-testid="create-user-btn">
                <UserPlus size={16} className="mr-2" /> New User
              </Button>
            </div>

            {/* Create User Form */}
            {showCreateUser && (
              <Card className={`p-6 mb-6 ${cardBg}`} data-testid="create-user-form">
                <h3 className="text-lg font-semibold text-foreground mb-4">Create User</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className={labelCls}>Email *</Label>
                    <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={inputCls} placeholder="user@example.com" data-testid="user-email-input" />
                  </div>
                  <div>
                    <Label className={labelCls}>Name *</Label>
                    <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={inputCls} placeholder="John Doe" data-testid="user-name-input" />
                  </div>
                  <div>
                    <Label className={labelCls}>Auth Provider</Label>
                    <select value={newUser.auth_provider} onChange={(e) => setNewUser({ ...newUser, auth_provider: e.target.value })} className={selectCls} data-testid="user-auth-select">
                      <option value="local">Email + Password</option>
                      <option value="google">Google Only</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                  {newUser.auth_provider !== "google" && (
                    <div>
                      <Label className={labelCls}>Password</Label>
                      <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={inputCls} placeholder="Min. 8 characters" data-testid="user-password-input" />
                    </div>
                  )}
                  <div>
                    <Label className={labelCls}>Role</Label>
                    <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={selectCls}>
                      <option value="event_user">Event User</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <Button onClick={createUser} className="bg-primary hover:bg-primary/90 text-foreground" data-testid="submit-user-btn">Create</Button>
                  <Button variant="outline" onClick={() => setShowCreateUser(false)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground">Cancel</Button>
                </div>
              </Card>
            )}

            {/* User List */}
            <div className="space-y-2">
              {users.map(u => (
                <Card key={u.user_id} className={`p-4 ${cardBg} ${!u.is_active ? 'opacity-50' : ''} transition-all hover:border-[#3a3a4a]`} data-testid={`user-card-${u.user_id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${u.role === 'super_admin' ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                        <User size={16} className={u.role === 'super_admin' ? 'text-amber-400' : 'text-blue-400'} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground">{u.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === 'super_admin' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-blue-500/15 text-blue-400 border border-blue-500/20'}`}>
                            {u.role === 'super_admin' ? 'Admin' : 'User'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.auth_provider === 'google' ? 'bg-red-500/15 text-red-400 border border-red-500/20' : u.auth_provider === 'both' ? 'bg-primary/15 text-primary border border-primary/20' : 'bg-gray-500/15 text-muted-foreground border border-gray-500/20'}`}>
                            {u.auth_provider}
                          </span>
                          {!u.is_active && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/20">disabled</span>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">{u.email}</p>
                        {u.event_ids?.length > 0 && (
                          <p className="text-xs text-gray-600 mt-0.5">{u.event_ids.length} event(s) assigned</p>
                        )}
                      </div>
                    </div>
                    {u.user_id !== user?.user_id && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setEditingUser({ ...u, newPassword: "", targetEventIds: u.event_ids || [] })} className="border-border text-foreground hover:bg-white/5 hover:text-foreground h-8" data-testid={`edit-user-${u.user_id}`} title="Edit user">
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" onClick={() => deleteUser(u.user_id)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/20 h-8" title="Delete">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ============ PROFILE TAB ============ */}
        {activeTab === "profile" && (
          <div className="max-w-2xl">
            <h2 className="text-xl font-semibold text-foreground mb-6">My Profile</h2>

            {/* Profile Info */}
            <Card className={`p-6 mb-6 ${cardBg}`} data-testid="profile-info-card">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <User size={18} /> Account Information
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className={labelCls}><User size={13} className="inline mr-1" /> Name</Label>
                  <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} className={inputCls} data-testid="profile-name-input" />
                </div>
                <div>
                  <Label className={labelCls}><Mail size={13} className="inline mr-1" /> Email</Label>
                  <Input value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} className={inputCls} data-testid="profile-email-input" />
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20">{user?.role}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/20">{user?.auth_provider || 'local'}</span>
                </div>
                <Button 
                  onClick={updateProfile} 
                  disabled={profileLoading || (profileName === user?.name && profileEmail === user?.email)}
                  className="bg-primary hover:bg-primary/90 text-foreground" 
                  data-testid="save-profile-btn"
                >
                  <Check size={16} className="mr-2" /> Save Changes
                </Button>
              </div>
            </Card>

            {/* Change Password */}
            {user?.auth_provider !== "google" && (
              <Card className={`p-6 ${cardBg}`} data-testid="change-password-card">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Lock size={18} /> Change Password
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className={labelCls}>Current Password</Label>
                    <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputCls} placeholder="Enter current password" data-testid="current-password-input" />
                  </div>
                  <div>
                    <Label className={labelCls}>New Password</Label>
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="Min. 8 characters" data-testid="new-password-input" />
                  </div>
                  <div>
                    <Label className={labelCls}>Confirm New Password</Label>
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls} placeholder="Confirm new password" data-testid="confirm-password-input" />
                  </div>
                  <Button 
                    onClick={changePassword} 
                    disabled={profileLoading || !currentPassword || !newPassword || !confirmPassword}
                    className="bg-primary hover:bg-primary/90 text-foreground" 
                    data-testid="change-password-btn"
                  >
                    <Lock size={16} className="mr-2" /> Change Password
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ============ EDIT USER DIALOG ============ */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg">Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-2">
              <div>
                <Label className={labelCls}><Mail size={13} className="inline mr-1" />Email</Label>
                <Input value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} className={inputCls} data-testid="edit-user-email" />
              </div>
              <div>
                <Label className={labelCls}><User size={13} className="inline mr-1" />Name</Label>
                <Input value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className={inputCls} data-testid="edit-user-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}>Role</Label>
                  <select value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} className={selectCls} data-testid="edit-user-role">
                    <option value="event_user">Event User</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div>
                  <Label className={labelCls}>Auth Provider</Label>
                  <select value={editingUser.auth_provider} onChange={(e) => setEditingUser({ ...editingUser, auth_provider: e.target.value })} className={selectCls} data-testid="edit-user-auth">
                    <option value="local">Local (email+password)</option>
                    <option value="google">Google Only</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>
              {editingUser.auth_provider !== "google" && (
                <div>
                  <Label className={labelCls}><Lock size={13} className="inline mr-1" />New Password (leave blank to keep current)</Label>
                  <Input type="password" value={editingUser.newPassword || ""} onChange={(e) => setEditingUser({ ...editingUser, newPassword: e.target.value })} className={inputCls} placeholder="Enter new password..." data-testid="edit-user-password" />
                </div>
              )}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border">
                <Label className="text-foreground text-sm flex-1">Account Active</Label>
                <button
                  onClick={() => setEditingUser({ ...editingUser, is_active: !editingUser.is_active })}
                  className={`w-11 h-6 rounded-full transition-colors flex items-center px-0.5 ${editingUser.is_active ? 'bg-green-600 justify-end' : 'bg-gray-700 justify-start'}`}
                  data-testid="edit-user-active-toggle"
                >
                  <span className="w-5 h-5 bg-white rounded-full shadow-sm" />
                </button>
              </div>
              <div>
                <Label className={labelCls}>Event Access</Label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto p-2 rounded-lg bg-background border border-border">
                  {events.map(ev => {
                    const checked = editingUser.targetEventIds?.includes(ev.event_id);
                    return (
                      <label key={ev.event_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${checked ? 'bg-primary/10' : 'hover:bg-white/5'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const ids = editingUser.targetEventIds || [];
                            setEditingUser({
                              ...editingUser,
                              targetEventIds: e.target.checked ? [...ids, ev.event_id] : ids.filter(id => id !== ev.event_id)
                            });
                          }}
                          className="rounded border-border bg-transparent text-purple-600 focus:ring-primary"
                        />
                        <span className="text-sm text-gray-200">{ev.name}</span>
                        <span className="text-xs text-gray-600">({ev.slug})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingUser(null)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground">Cancel</Button>
            <Button onClick={saveUserEdit} className="bg-primary hover:bg-primary/90 text-foreground" data-testid="save-user-edit-btn">
              <Check size={15} className="mr-1.5" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DUPLICATE EVENT DIALOG ============ */}
      <Dialog open={!!duplicatingEvent} onOpenChange={() => setDuplicatingEvent(null)}>
        <DialogContent className="bg-card border-border text-foreground max-w-md" data-testid="duplicate-event-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg">Duplicate Event</DialogTitle>
          </DialogHeader>
          {duplicatingEvent && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-primary">Cloning from: <strong className="text-foreground">{duplicatingEvent.name}</strong></p>
                <p className="text-xs text-muted-foreground mt-1">Judges and theme settings will be duplicated.</p>
              </div>
              <div>
                <Label className={labelCls}>New Event Name *</Label>
                <Input value={dupData.name} onChange={(e) => setDupData({ ...dupData, name: e.target.value, slug: slugify(e.target.value) })} className={inputCls} data-testid="dup-event-name" />
              </div>
              <div>
                <Label className={labelCls}>Slug</Label>
                <Input value={dupData.slug} onChange={(e) => setDupData({ ...dupData, slug: e.target.value })} className={inputCls} data-testid="dup-event-slug" />
              </div>
              <div>
                <Label className={labelCls}>Description</Label>
                <Input value={dupData.description} onChange={(e) => setDupData({ ...dupData, description: e.target.value })} className={inputCls} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDuplicatingEvent(null)} className="border-border text-foreground hover:bg-white/5 hover:text-foreground">Cancel</Button>
            <Button onClick={duplicateEvent} className="bg-primary hover:bg-primary/90 text-foreground" data-testid="confirm-dup-btn">
              <Copy size={15} className="mr-1.5" /> Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
